import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowLeft, Search, Filter, Lightbulb, Users, Eye, Volume2, Accessibility, Utensils } from 'lucide-react';

interface ImprovementSuggestionsProps {
  onBack: () => void;
}

const allSuggestions = [
  {
    id: 1,
    title: "Rampe di accesso modulari in alluminio",
    description: "Rampe portatili e modulari per superare dislivelli fino a 60cm",
    category: "Motoria",
    impact: "Alta",
    improvementRate: "18/36",
    estimatedCost: "€2,500 - €5,000",
    implementation: "2-3 settimane",
    icon: <Accessibility className="w-5 h-5" />,
    color: "blue"
  },
  {
    id: 2,
    title: "Sistema di amplificazione audio portatile",
    description: "Impianto audio wireless per eventi e presentazioni",
    category: "Uditiva",
    impact: "Media",
    improvementRate: "13/36",
    estimatedCost: "€800 - €1,500",
    implementation: "1 settimana",
    icon: <Volume2 className="w-5 h-5" />,
    color: "purple"
  },
  {
    id: 3,
    title: "Segnaletica tattile e braille",
    description: "Mappe tattili e segnaletica in braille per orientamento",
    category: "Visiva",
    impact: "Alta",
    improvementRate: "15/36",
    estimatedCost: "€1,200 - €3,000",
    implementation: "3-4 settimane",
    icon: <Eye className="w-5 h-5" />,
    color: "orange"
  },
  {
    id: 4,
    title: "Ascensori con sintesi vocale",
    description: "Sistema audio per annunci vocali dei piani negli ascensori",
    category: "Uditiva",
    impact: "Alta",
    improvementRate: "22/36",
    estimatedCost: "€3,500 - €7,000",
    implementation: "4-6 settimane",
    icon: <Volume2 className="w-5 h-5" />,
    color: "purple"
  },
  {
    id: 5,
    title: "Pannelli informativi ad alto contrasto",
    description: "Segnaletica con contrasto cromatico elevato per ipovedenti",
    category: "Visiva",
    impact: "Media",
    improvementRate: "11/36",
    estimatedCost: "€600 - €1,200",
    implementation: "1-2 settimane",
    icon: <Eye className="w-5 h-5" />,
    color: "orange"
  },
  {
    id: 6,
    title: "Bagni accessibili con maniglioni",
    description: "Installazione di supporti e maniglioni per persone con mobilità ridotta",
    category: "Motoria",
    impact: "Alta",
    improvementRate: "25/36",
    estimatedCost: "€1,800 - €3,500",
    implementation: "2-3 settimane",
    icon: <Accessibility className="w-5 h-5" />,
    color: "blue"
  },
  {
    id: 7,
    title: "Menu in braille per ristoranti",
    description: "Menu tattili per non vedenti nelle strutture di ristorazione",
    category: "Alimentare",
    impact: "Media",
    improvementRate: "8/36",
    estimatedCost: "€200 - €500",
    implementation: "1 settimana",
    icon: <Utensils className="w-5 h-5" />,
    color: "green"
  },
  {
    id: 8,
    title: "Parcheggi con segnaletica chiara",
    description: "Stalli per disabili con segnaletica orizzontale e verticale migliorata",
    category: "Motoria",
    impact: "Media",
    improvementRate: "14/36",
    estimatedCost: "€400 - €800",
    implementation: "1 settimana",
    icon: <Accessibility className="w-5 h-5" />,
    color: "blue"
  },
  {
    id: 9,
    title: "Sistema di induzione magnetica",
    description: "Loop magnetico per persone con apparecchi acustici",
    category: "Uditiva",
    impact: "Alta",
    improvementRate: "9/36",
    estimatedCost: "€1,500 - €3,000",
    implementation: "2-3 settimane",
    icon: <Volume2 className="w-5 h-5" />,
    color: "purple"
  },
  {
    id: 10,
    title: "Comunicazione semplificata",
    description: "Pannelli informativi con linguaggio semplice e pittogrammi",
    category: "Cognitiva",
    impact: "Media",
    improvementRate: "12/36",
    estimatedCost: "€800 - €1,500",
    implementation: "2-3 settimane",
    icon: <Users className="w-5 h-5" />,
    color: "cyan"
  },
  {
    id: 11,
    title: "Pavimentazione antiscivolo",
    description: "Rivestimenti antiscivolo per zone a rischio cadute",
    category: "Motoria",
    impact: "Alta",
    improvementRate: "19/36",
    estimatedCost: "€2,000 - €4,000",
    implementation: "3-4 settimane",
    icon: <Accessibility className="w-5 h-5" />,
    color: "blue"
  },
  {
    id: 12,
    title: "Illuminazione adattiva",
    description: "Sistemi di illuminazione regolabile per diverse esigenze visive",
    category: "Visiva",
    impact: "Media",
    improvementRate: "7/36",
    estimatedCost: "€1,200 - €2,500",
    implementation: "2-4 settimane",
    icon: <Eye className="w-5 h-5" />,
    color: "orange"
  }
];

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'Motoria': return 'text-blue-600 bg-blue-50 border-blue-200';
    case 'Visiva': return 'text-orange-600 bg-orange-50 border-orange-200';
    case 'Uditiva': return 'text-purple-600 bg-purple-50 border-purple-200';
    case 'Cognitiva': return 'text-cyan-600 bg-cyan-50 border-cyan-200';
    case 'Alimentare': return 'text-green-600 bg-green-50 border-green-200';
    default: return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

const getImpactColor = (impact: string) => {
  switch (impact) {
    case 'Alta': return 'bg-red-100 text-red-800';
    case 'Media': return 'bg-yellow-100 text-yellow-800';
    case 'Bassa': return 'bg-gray-100 text-gray-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

export function ImprovementSuggestions({ onBack }: ImprovementSuggestionsProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [impactFilter, setImpactFilter] = useState("all");
  const [sampleStructures, setSampleStructures] = useState<any[]>([]);
  const [loadingStructures, setLoadingStructures] = useState(false);

  // Load a small sample from the private "structures" collection to anchor suggestions to live data
  useEffect(() => {
    let mounted = true;
    async function loadStructuresSnapshot() {
      setLoadingStructures(true);
      try {
        if (mounted) setSampleStructures([]);
      } catch {
        if (mounted) setSampleStructures([]);
      } finally {
        if (mounted) setLoadingStructures(false);
      }
    }
    loadStructuresSnapshot();
    return () => { mounted = false; };
  }, []);

  const resolveStructureName = (structure: any) =>
    structure?.structure?.accommodation_data?.accommodation_name ||
    structure?.name ||
    structure?.id ||
    'Structure';

  const resolveStructureType = (structure: any) =>
    structure?.structure?.accommodation_data?.type ||
    structure?.type ||
    '';

  const filteredSuggestions = allSuggestions.filter((suggestion) => {
    const matchesSearch = !searchQuery.trim() || 
      suggestion.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      suggestion.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = categoryFilter === "all" || suggestion.category === categoryFilter;
    const matchesImpact = impactFilter === "all" || suggestion.impact === impactFilter;
    
    return matchesSearch && matchesCategory && matchesImpact;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50">
      {/* Fixed Header */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-sm border-b border-border/40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onBack}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Torna alla dashboard
              </Button>
              <div className="h-6 w-px bg-border"></div>
              <div className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-500" />
                <h1 className="text-xl font-medium">Tutti i suggerimenti di miglioramento</h1>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Cerca suggerimenti..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[160px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le categorie</SelectItem>
                  <SelectItem value="Motoria">Motoria</SelectItem>
                  <SelectItem value="Visiva">Visiva</SelectItem>
                  <SelectItem value="Uditiva">Uditiva</SelectItem>
                  <SelectItem value="Cognitiva">Cognitiva</SelectItem>
                  <SelectItem value="Alimentare">Alimentare</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={impactFilter} onValueChange={setImpactFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Impatto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli impatti</SelectItem>
                  <SelectItem value="Alta">Alta</SelectItem>
                  <SelectItem value="Media">Media</SelectItem>
                  <SelectItem value="Bassa">Bassa</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6">
        {/* Structures snapshot from Firestore (structures) */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-m">Snapshot strutture</CardTitle>
            <CardDescription>Dati dalla collezione Firestore "structures"</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStructures ? (
              <p className="text-s text-muted-foreground">Caricamento strutture...</p>
            ) : sampleStructures.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {sampleStructures.slice(0, 6).map((structure) => (
                  <div key={structure.id} className="p-3 rounded-lg border bg-white">
                    <div className="font-medium leading-tight">{resolveStructureName(structure)}</div>
                    <div className="text-ss text-muted-foreground mt-1">{resolveStructureType(structure) || 'Tipo sconosciuto'}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-s text-muted-foreground">Nessuna struttura disponibile da Firestore al momento.</p>
            )}
          </CardContent>
        </Card>

        {/* Results Summary */}
        <div className="mb-6">
          <p className="text-s text-muted-foreground">
            Mostrati {filteredSuggestions.length} di {allSuggestions.length} suggerimenti di miglioramento
          </p>
        </div>

        {/* Suggestions Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredSuggestions.map((suggestion) => (
            <Card key={suggestion.id} className="hover:shadow-lg transition-all duration-200 border-0 shadow-md">
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getCategoryColor(suggestion.category)}`}>
                    {suggestion.icon}
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="outline" className={`text-ss ${getCategoryColor(suggestion.category)}`}>
                      {suggestion.category}
                    </Badge>
                    <Badge className={`text-ss ${getImpactColor(suggestion.impact)}`}>
                      Impatto: {suggestion.impact}
                    </Badge>
                  </div>
                </div>
                <CardTitle className="text-l leading-tight">{suggestion.title}</CardTitle>
                <CardDescription className="text-s line-clamp-2">
                  {suggestion.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 gap-3 mb-4">
                  <div className="flex items-center justify-between text-ss">
                    <span className="text-muted-foreground">Tasso miglioramento:</span>
                    <span className="font-medium">{suggestion.improvementRate}</span>
                  </div>
                  <div className="flex items-center justify-between text-ss">
                    <span className="text-muted-foreground">Costo stimato:</span>
                    <span className="font-medium">{suggestion.estimatedCost}</span>
                  </div>
                  <div className="flex items-center justify-between text-ss">
                    <span className="text-muted-foreground">Implementazione:</span>
                    <span className="font-medium">{suggestion.implementation}</span>
                  </div>
                </div>
                <Button 
                  className="w-full bg-gradient-travelin hover:bg-gradient-travelin-cool text-white"
                  size="sm"
                >
                  Adotta soluzione
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* No Results */}
        {filteredSuggestions.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Search className="w-6 h-6 text-muted-foreground" />
            </div>
            <h3 className="text-l font-medium mb-2">Nessun suggerimento trovato</h3>
            <p className="text-muted-foreground">Prova a modificare i termini di ricerca o i filtri</p>
          </div>
        )}
      </div>
    </div>
  );
}
